/*
 * PDP Surgical — Room preview integration.
 *
 * Mirrors the readymade-frame-pdp 3D room preview integration, scoped for
 * the surgical PDP's media-gallery host. The behaviour, markup classes and
 * UX are identical to assets/readymade-frame-pdp.js's room-preview block;
 * the only difference is the data source — surgical reads frame state from
 * window.skuPickerInstance + the page's <gallery-wall-frame-mock> element
 * instead of the readymade-frame-pdp's RMFrameProductData JSON blob.
 *
 * Lifecycle
 *   1. Wait for media-gallery[data-new-pdp] + skuPickerInstance.
 *   2. Clone the #pdp-surgical-room-preview-tpl template into the gallery.
 *   3. On window.load: load THREE + the room-3d-preview-bundle, mount the
 *      synthetic-wall default scene. On any failure, leave the slideshow
 *      as the only view (no overlay activation).
 *   4. Per-slide pills live inside the FRAME and ROOM slides — each owns
 *      a Frame ↔ 3D toggle (slot frame3d) or Example wall ↔ Your wall
 *      toggle (slot wall). A single bundle instance moves between the two
 *      slots; entering one disposes the other. No global mode pill.
 *   5. Photo upload runs the same /api/room-analysis flow, then mounts the
 *      real-room composite. After upload, the segmented "Your room / Plain
 *      wall" picker appears.
 */

(function () {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────

  const BACKEND_HOST = window.RMF_SHOP_CHAT_AGENT_HOST
    || 'https://shop-chat-agent.iamfy.tech/gallery-wall';

  const THREE_MODULE_SRC = window.RMF_THREE_SRC
    || 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';

  function bundleSrc() {
    return `${BACKEND_HOST}/room-3d-preview-bundle.js`;
  }

  // ── Cross-PDP wall-analysis cache ───────────────────────────────────────
  // Shared with readymade-frame-pdp.js: same localStorage key, same TTL,
  // same shape. Once a user uploads a wall photo on any PDP, every other
  // PDP can restore "Your wall" instantly without re-analysing.
  const WALL_ANALYSIS_STORAGE_KEY = 'fy:wall-analysis';
  const WALL_ANALYSIS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

  function loadPersistedWallAnalysis() {
    try {
      const raw = localStorage.getItem(WALL_ANALYSIS_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.analysis || !parsed.savedAt) return null;
      if (Date.now() - parsed.savedAt > WALL_ANALYSIS_TTL_MS) {
        localStorage.removeItem(WALL_ANALYSIS_STORAGE_KEY);
        return null;
      }
      return parsed.analysis;
    } catch { return null; }
  }

  function persistWallAnalysis(analysis) {
    if (!analysis) return;
    try {
      localStorage.setItem(WALL_ANALYSIS_STORAGE_KEY, JSON.stringify({
        analysis,
        savedAt: Date.now(),
      }));
    } catch { /* storage full / private mode — non-fatal */ }
  }

  // ── State ───────────────────────────────────────────────────────────────

  const roomPreviewState = {
    bundleReady: null,
    analysis: null,
    instance: null,        // Live bundle handle (Room3DPreview.mount result), or null
    bundleSlot: null,      // 'frame3d' | 'wall' | null — which slot owns the instance
    lastPayload: null,
    flowAbort: null,       // AbortController — aborts a superseded startRoomFlow
  };

  // Per-slide preview slots. Each slot has a default-content element
  // (canvas-frame-mock on slide 0, room-background-mock on slide 1) plus
  // a 3D overlay that's hidden until the user enters that slot's 3D mode.
  // Only one slot can hold the live bundle at a time — entering one
  // disposes the other.
  const slots = {
    frame3d: {
      name:       'frame3d',
      slideRole:  'frame-mock',
      templateId: 'pdp-surgical-slide0-tpl',
      slideEl:    null,
      pillEl:     null,
      overlayEl:  null,
      stageEl:    null,
      statusEl:   null,
      defaultEl:  null, // <gallery-wall-frame-mock>
      zoomBtnEl:  null, // .pdp-slide-zoom-btn (default-mode only)
      mode:       'default', // 'default' | '3d'
    },
    wall: {
      name:       'wall',
      slideRole:  'room-mock',
      templateId: 'pdp-surgical-slide1-tpl',
      slideEl:    null,
      pillEl:     null,
      overlayEl:  null,
      stageEl:    null,
      statusEl:   null,
      defaultEl:  null, // <room-background-mock>
      fileInputEl: null,
      tipEl:       null,
      mode:       'default',
    },
  };

  let mediaGallery = null;

  // ── Boot — find both slides + clone their templates ──────────────────

  function findGallery() {
    return document.querySelector('media-gallery[data-new-pdp]')
      || document.querySelector('.product-information--surgical media-gallery');
  }

  function init() {
    if (mediaGallery) return true; // idempotent

    const gallery = findGallery();
    if (!gallery) return false;

    mediaGallery = gallery;
    if (!gallery.dataset.heroMode) gallery.dataset.heroMode = 'print';
    gallery.style.position = gallery.style.position || 'relative';

    // Locate each slot's slide, clone its template once, and capture refs.
    let anySlotReady = false;
    for (const slotName of Object.keys(slots)) {
      const slot = slots[slotName];
      const slide = gallery.querySelector(`slideshow-slide[data-slide-role="${slot.slideRole}"]`);
      if (!slide) continue;

      slot.slideEl = slide;
      if (slide.style.position === '' && getComputedStyle(slide).position === 'static') {
        slide.style.position = 'relative';
      }

      const tpl = document.getElementById(slot.templateId);
      if (!tpl) continue;
      if (!slide.querySelector(`.pdp-3d-overlay--${slotName}`)) {
        slide.appendChild(tpl.content.cloneNode(true));
      }

      slot.pillEl    = slide.querySelector(`.pdp-mode-tabs--${slotName}`);
      slot.overlayEl = slide.querySelector(`.pdp-3d-overlay--${slotName}`);
      slot.stageEl   = slot.overlayEl?.querySelector('.rmf-room-preview__stage');
      slot.statusEl  = slot.overlayEl?.querySelector('.rmf-room-preview__status');
      slot.defaultEl = slotName === 'frame3d'
        ? slide.querySelector('gallery-wall-frame-mock')
        : slide.querySelector('room-background-mock');

      // Slide 0 hosts an interactive canvas frame mock (wall-colour picker
      // drags, frame previews) so click-to-zoom can't share the slide —
      // taps would fight the canvas + the Frame/3D pill. Strip the slide's
      // declarative on:click handler permanently; the zoom button below
      // provides the explicit lightbox affordance.
      if (slotName === 'frame3d') {
        slot.zoomBtnEl = slide.querySelector('.pdp-slide-zoom-btn');
        const slideOnClick = slide.getAttribute('on:click');
        if (slideOnClick && slideOnClick !== 'none') {
          slide.dataset.zoomOnClickOriginal = slideOnClick;
        }
        slide.setAttribute('on:click', 'none');
      }

      if (slotName === 'wall') {
        slot.fileInputEl    = slide.querySelector('#pdp-surgical-wall-input');
        slot.tipEl          = slide.querySelector('#pdp-surgical-wall-tip');
      }
      anySlotReady = true;
    }
    if (!anySlotReady) return false;

    bindSlotControls();
    bindConfiguratorEvents();
    bindMeasureToggle();

    // Stamp the initial mode on each slide so CSS selectors (e.g. the
    // slide-0 zoom button visible only in default mode) match before the
    // first pill interaction.
    applySlotVisibility(slots.frame3d);
    applySlotVisibility(slots.wall);

    // Cross-PDP wall-analysis restore — flips slot.wall's pill to its
    // 3-segment state (Example | Your wall | 📷) without auto-entering wall
    // mode, so a returning user sees the curated example with the pill
    // broadcasting "your wall is ready, tap to see it."
    const persistedAnalysis = loadPersistedWallAnalysis();
    if (persistedAnalysis) roomPreviewState.analysis = persistedAnalysis;
    syncSlotPill('frame3d');
    syncSlotPill('wall');

    // 3D bundle (incl. Three.js ~168KB) loads ONLY when the user activates the
    // 3D toggle (enterSlot → mountRoomPreview → loadPreviewBundle). No idle/intent
    // warm — keeps Three.js off the critical path on PDPs that never enter 3D.

    return true;
  }

  // Simulate a horizontal pointer drag on the 3D canvas to rotate the frame.
  // Mirrors the identically-named function in readymade-frame-pdp.js.
  function simulateCanvasDrag(direction) {
    // Rotate buttons only exist on the frame3d slot's overlay; pick the
    // canvas of whichever slot the bundle currently lives in.
    const slot = roomPreviewState.bundleSlot ? slots[roomPreviewState.bundleSlot] : null;
    const canvas = window.Room3DPreview?.viewer?.renderer?.domElement
      || roomPreviewState.instance?.viewer?.renderer?.domElement
      || slot?.stageEl?.querySelector('canvas');
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = direction === 'left' ? -18 : 18;
    const pointerId = Math.floor(Math.random() * 1e6);
    const opts = (x, y, buttons) => ({
      pointerId, pointerType: 'mouse', isPrimary: true,
      clientX: x, clientY: y, screenX: x, screenY: y,
      button: 0, buttons,
      bubbles: true, cancelable: true,
    });
    const origCapture = canvas.setPointerCapture;
    const origRelease = canvas.releasePointerCapture;
    canvas.setPointerCapture = () => {};
    canvas.releasePointerCapture = () => {};
    canvas.dispatchEvent(new PointerEvent('pointerdown', opts(cx, cy, 1)));
    const STEPS = 8;
    for (let i = 1; i <= STEPS; i++) {
      canvas.dispatchEvent(new PointerEvent('pointermove', opts(cx + (dx * i) / STEPS, cy, 1)));
    }
    canvas.dispatchEvent(new PointerEvent('pointerup', opts(cx + dx, cy, 0)));
    canvas.setPointerCapture = origCapture;
    canvas.releasePointerCapture = origRelease;
  }

  function ready() {
    if (init()) return;
    // Retry until media-gallery + skuPicker exist (sections load async).
    let attempts = 0;
    const iv = setInterval(() => {
      attempts += 1;
      if (init() || attempts > 40) clearInterval(iv);
    }, 150);
    // Mutation fallback for slow-mounting sections.
    const mo = new MutationObserver(() => { if (init()) mo.disconnect(); });
    mo.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => mo.disconnect(), 12000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
  document.addEventListener('shopify:section:load', ready);

  // ── Frame / size data sourcing ─────────────────────────────────────────
  //
  // Surgical reads the live configurator state instead of a per-PDP JSON
  // blob — same source the surgical-config picker uses. When skuPicker
  // hasn't booted yet (early page load) we fall back to the page's static
  // gallery-wall-frame-mock attributes.

  function getSkuPicker() {
    return window.skuPickerInstance || null;
  }

  function getPageMockEl() {
    // The main interactive frame mock on the surgical PDP — rendered by
    // single-print-frame-mock.liquid into the FRAME slide. Surgical-config
    // also uses this as the canonical source for moulding/wall textures.
    return document.querySelector('gallery-wall-frame-mock:not([data-corner-crop])');
  }

  function getCurrentFramingOption() {
    const sp = getSkuPicker();
    if (!sp) return null;
    try {
      const cv = sp.currentVariantId;
      const variantData = sp.variantConfigurators?.variants?.[cv];
      const framings = variantData?.options?.framing || sp.data?.options?.framing || [];
      const framingHandle = sp.state?.confirmed?.framing_option;
      return framings.find(f => f.handle === framingHandle) || null;
    } catch { return null; }
  }

  function getArtProduct() {
    // Canonical canvas signal — mirrors sku-picker.js (variantData.artProduct
    // === 'canvas-print'). The legacy data-product-type attribute lived on
    // <gallery-wall-frame-mock>, which the surgical PDP no longer renders (the
    // hero is a CSS mock-image), so reading productType off the page mock
    // returned null and canvas frames fell back to the recessed art-print path.
    const sp = getSkuPicker();
    if (!sp) return null;
    try {
      const cv = sp.currentVariantId;
      const variantData = sp.variantConfigurators?.variants?.[cv];
      return variantData?.artProduct || sp.data?.artProduct || null;
    } catch { return null; }
  }

  function getCurrentFrameColor() {
    const sp = getSkuPicker();
    if (!sp) return null;
    try {
      const colorHandle = sp.state?.confirmed?.frame_color;
      const fr = getCurrentFramingOption();
      if (!fr) return null;
      const colors = fr.colors || [];
      return colors.find(c => c.handle === colorHandle) || colors[0] || null;
    } catch { return null; }
  }

  function getBorderOption() {
    const sp = getSkuPicker();
    return sp?.state?.confirmed?.border_option || 'no-border';
  }

  function getFramingHandle() {
    const sp = getSkuPicker();
    return sp?.state?.confirmed?.framing_option || '';
  }

  function isUnframed() {
    return getFramingHandle() === 'unframed';
  }

  function pickSizeForPreview() {
    const sp = getSkuPicker();
    const handle = String(sp?.state?.confirmed?.size_option || '');
    // Configurator size handles look like "16-x-20" or "16x20" — split on
    // either separator (same logic as readymade-frame-pdp.pickSizeForPreview).
    const parts = handle.split(/[-x×]/).map(Number).filter(n => n > 0);
    if (parts.length >= 2) return { width_inches: parts[0], height_inches: parts[1] };
    // Fall back to the page mock's data-artwork-width if the size handle
    // is missing (e.g. before configurator boot completes).
    const pm = getPageMockEl();
    const w = parseFloat(pm?.dataset.artworkWidth || '');
    const aspect = parseFloat(pm?.dataset.artworkAspect || '1');
    if (w && aspect) return { width_inches: w, height_inches: w / aspect };
    return { width_inches: 16, height_inches: 20 };
  }

  function buildFrameConfigPayload() {
    const color = getCurrentFrameColor();
    const framing = getCurrentFramingOption();
    const pageMock = getPageMockEl();
    const unframed = isUnframed();
    const border = getBorderOption();
    const isCanvas = getArtProduct() === 'canvas-print'
      || (pageMock?.dataset.productType || 'art_print') === 'canvas';

    const profilePointsRaw = framing?.profile_points ?? null;
    const profilePoints = typeof profilePointsRaw === 'string'
      ? (() => { try { return JSON.parse(profilePointsRaw); } catch { return null; } })()
      : (profilePointsRaw || null);

    return {
      print_only: unframed,
      tile_url: unframed ? null : (color?.moulding_tile || pageMock?.dataset.mouldingTile || null),
      profile_width_mm: unframed ? 0 : (framing?.profile_width_mm || parseFloat(color?.moulding_width || pageMock?.dataset.mouldingWidth || 15)),
      profile_depth_mm: framing?.profile_depth_mm || 0,
      profile_points: profilePoints,
      lip_depth_normalized: framing?.lip_depth_normalized || 0,
      hex: unframed ? null : (color?.color_value || pageMock?.dataset.frameColor || '#1a1612'),
      metalness: color?.metalness ?? 0,
      roughness: color?.roughness ?? 0.6,
      outer_face_material: color?.outer_face_material || (isCanvas ? 'canvas' : 'tile'),
      inner_face_material: color?.inner_face_material || (isCanvas ? 'canvas' : 'tile'),
      mountOption: (border === 'mount' || border === 'border') ? 'single-mount' : 'no-mount',
      borderWidth: border === 'mount' ? 4 : border === 'border' ? 2 : 0,
      // Physical mount/border width in mm — must be sent or the 3D bundle
      // falls back to its 50mm default. Mirrors the 2D frame-mock, which
      // renders data-border-width in cm (mount → 4cm, border → 2cm).
      mountWidthMm: border === 'mount' ? 40 : border === 'border' ? 20 : 0,
      mountColor: 0xfafaf8,
      includeGlazing: !isCanvas && !unframed,
      // Canvas render mode: gallery-wrapped canvas in a thin tray/float frame.
      // The renderer (room-viewer / three-viewer) builds a wrapped-canvas block
      // at the frame's front face instead of a recessed framed-print stack.
      is_canvas: isCanvas,
      canvas_depth_mm: 38,
    };
  }

  function currentArtworkUrl() {
    const pageMock = getPageMockEl();
    // The legacy canvas frame-mock (getPageMockEl) was replaced by a CSS
    // mock-image hero, so the artwork URL now lives on <style-picker>
    // (data-artwork-url) / the mock-image (data-artwork-src). Fall back to those
    // so the 3D view textures the artwork instead of rendering an empty frame.
    return pageMock?.dataset.artworkUrl
      || document.querySelector('style-picker')?.dataset.artworkUrl
      || document.querySelector('[data-artwork-src]')?.dataset.artworkSrc
      || null;
  }

  function getWallColor() {
    const pageMock = getPageMockEl();
    return pageMock?.dataset.wallColor || pageMock?.wallColor || '#f5f0e8';
  }

  function currentConfigSignature() {
    const sp = getSkuPicker();
    const c = sp?.state?.confirmed || {};
    return [
      c.size_option || '',
      c.border_option || '',
      c.framing_option || '',
      c.frame_color || '',
      sp?.currentVariantId || '',
    ].join('|');
  }

  // ── Bundle loading (Three + room-3d-preview-bundle) ───────────────────

  // Slot-scoped: each slot's overlay carries its own loading/error chrome.
  // Status text appears centred in the overlay; the upload pill busy state
  // lives on the slot's wall tab via setUploadBusy.
  function setPreviewStatus(slot, kind, msg = '') {
    if (!slot || !slot.overlayEl) return;
    slot.overlayEl.classList.remove('rmf-room-preview--loading', 'rmf-room-preview--error');
    if (kind === 'loading') slot.overlayEl.classList.add('rmf-room-preview--loading');
    if (kind === 'error')   slot.overlayEl.classList.add('rmf-room-preview--error');
    if (slot.statusEl) slot.statusEl.textContent = msg;
  }

  // Slot lifecycle ─────────────────────────────────────────────────────────

  function applySlotVisibility(slot) {
    if (!slot) return;
    const is3d = slot.mode === '3d';
    if (slot.overlayEl) slot.overlayEl.setAttribute('aria-hidden', is3d ? 'false' : 'true');
    // Hide the slot's default content (canvas-mock or room-background-mock)
    // when the 3D overlay is active — and restore it on exit.
    if (slot.defaultEl) slot.defaultEl.style.visibility = is3d ? 'hidden' : '';
    if (slot.slideEl) slot.slideEl.dataset.slotMode = slot.mode;
    // Slot-1 specific: disable the slide's zoom-on-click when 3D is active
    // so taps don't open the lightbox over the bundle canvas.
    if (slot.name === 'wall' && slot.slideEl) {
      if (is3d) {
        const onClick = slot.slideEl.getAttribute('on:click');
        if (onClick && onClick !== 'none') slot.slideEl.dataset.zoomOnClick = onClick;
        slot.slideEl.setAttribute('on:click', 'none');
      } else if (slot.slideEl.dataset.zoomOnClick) {
        slot.slideEl.setAttribute('on:click', slot.slideEl.dataset.zoomOnClick);
      }
    }
  }

  // Compose a payload appropriate for the slot's 3D content:
  //   frame3d → synthetic-wall orbit view (always available)
  //   wall    → real-room composite (requires roomPreviewState.analysis)
  function payloadForSlot(slotName) {
    if (slotName === 'frame3d') return buildDefaultPayload();
    if (slotName === 'wall' && roomPreviewState.analysis) {
      return buildMountPayload(roomPreviewState.analysis);
    }
    return null;
  }

  // Pill click → enter the slot's 3D mode. If the bundle is in the other
  // slot, mountRoomPreview() disposes it and resets the pill on the way.
  async function enterSlot(slotName) {
    const slot = slots[slotName];
    if (!slot) return;
    const payload = payloadForSlot(slotName);
    if (!payload) return; // wall slot needs an analysis first
    try {
      setUploadBusy(false);
      await mountRoomPreview(slotName, payload);
    } catch (err) {
      console.warn(`[surgical-room-preview] enter ${slotName} failed`, err);
      setPreviewStatus(slot, 'error', 'Could not load 3D preview.');
    }
  }

  // Pill click → return to the slot's default content. Dispose the bundle
  // if this slot owns it.
  function exitSlot(slotName) {
    const slot = slots[slotName];
    if (!slot) return;
    if (roomPreviewState.bundleSlot === slotName) {
      try { roomPreviewState.instance?.dispose?.(); } catch { /* noop */ }
      roomPreviewState.instance = null;
      roomPreviewState.bundleSlot = null;
      roomPreviewState.lastPayload = null;
    }
    slot.mode = 'default';
    if (slot.overlayEl) slot.overlayEl.classList.remove('rmf-room-preview--has-photo');
    applySlotVisibility(slot);
    syncSlotPill(slotName);
  }

  // Reflect the slot's mode + analysis state on its pill: active segment,
  // labels, and the has-wall class that reveals the change-photo icon on
  // the wall slot.
  function syncSlotPill(slotName) {
    const slot = slots[slotName];
    if (!slot || !slot.pillEl) return;
    const hasWall = !!roomPreviewState.analysis;

    if (slotName === 'wall') {
      slot.pillEl.classList.toggle('pdp-mode-tabs--has-wall', hasWall);
      const exampleLabel = slot.pillEl.querySelector('[data-pill-action="example"] .pdp-mode-tab__label');
      const wallLabel    = slot.pillEl.querySelector('[data-pill-action="wall"] .pdp-mode-tab__label');
      const wallTab      = slot.pillEl.querySelector('[data-pill-action="wall"]');
      // Locale-aware labels — sourced from data-* attributes set by Liquid.
      const d = slot.pillEl.dataset;
      // Don't clobber the busy label — setUploadBusy owns it.
      if (wallTab && !wallTab.classList.contains('pdp-mode-tab--busy')) {
        if (exampleLabel) exampleLabel.textContent = hasWall ? (d.labelExample || 'Example') : (d.labelExampleWall || 'Example wall');
        if (wallLabel)    wallLabel.textContent    = hasWall ? (d.labelYourWall || 'Your wall') : (d.labelSeeOnWall || 'See it on your wall');
      }
    }

    const activeAction = slotName === 'frame3d'
      ? (slot.mode === '3d' ? '3d' : 'frame')
      : (slot.mode === '3d' ? 'wall' : 'example');
    slot.pillEl.querySelectorAll('[data-pill-action]').forEach(btn => {
      const action = btn.dataset.pillAction;
      if (action !== 'frame' && action !== '3d' && action !== 'example' && action !== 'wall') return;
      const active = action === activeAction;
      btn.classList.toggle('pdp-mode-tab--active', active);
      btn.setAttribute('aria-selected', String(active));
    });
  }

  function loadScriptOnce(src, isReady) {
    if (isReady()) return Promise.resolve();
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') return Promise.resolve();
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', () => isReady() ? resolve() : reject(new Error(`Script loaded but ready check failed: ${src}`)));
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      });
    }
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = () => {
        s.dataset.loaded = 'true';
        isReady() ? resolve() : reject(new Error(`Script loaded but ready check failed: ${src}`));
      };
      s.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(s);
    });
  }

  function loadThreeModule() {
    if (window.THREE) return Promise.resolve();
    return import(/* @vite-ignore */ THREE_MODULE_SRC).then(mod => {
      window.THREE = mod;
    });
  }

  function loadPreviewBundle() {
    if (window.Room3DPreview?.mount) return Promise.resolve();
    if (roomPreviewState.bundleReady) return roomPreviewState.bundleReady;
    roomPreviewState.bundleReady = loadThreeModule()
      .then(() => loadScriptOnce(bundleSrc(), () => !!window.Room3DPreview?.mount))
      .catch((err) => {
        // Don't cache the rejection — a transient CDN failure would otherwise
        // block the 3D preview for the rest of the page session.
        roomPreviewState.bundleReady = null;
        throw err;
      });
    return roomPreviewState.bundleReady;
  }

  // ── Mount / refresh ────────────────────────────────────────────────────

  // Mount the bundle into a specific slot's overlay. If the bundle is
  // currently live in a different slot, dispose it first and reset that
  // slot back to its default view (so we never leave a stale pill state
  // claiming "Your wall" when the bundle has been moved elsewhere).
  async function mountRoomPreview(slotName, payload) {
    const slot = slots[slotName];
    if (!slot || !slot.stageEl) throw new Error(`mountRoomPreview: unknown slot ${slotName}`);

    if (roomPreviewState.instance && roomPreviewState.bundleSlot && roomPreviewState.bundleSlot !== slotName) {
      const otherSlot = slots[roomPreviewState.bundleSlot];
      try { roomPreviewState.instance.dispose?.(); } catch { /* noop */ }
      roomPreviewState.instance = null;
      if (otherSlot) {
        otherSlot.mode = 'default';
        applySlotVisibility(otherSlot);
        syncSlotPill(otherSlot.name);
      }
    } else if (roomPreviewState.instance) {
      try { roomPreviewState.instance.dispose?.(); } catch { /* noop */ }
      roomPreviewState.instance = null;
    }

    await loadPreviewBundle();
    roomPreviewState.instance = await window.Room3DPreview.mount(slot.stageEl, payload);
    if (payload._default) {
      roomPreviewState.instance.setWallColor?.(payload.wallColor || '#f5f0e8');
    }
    roomPreviewState.lastPayload = payload;
    roomPreviewState.bundleSlot = slotName;

    slot.mode = '3d';
    slot.overlayEl.classList.toggle('rmf-room-preview--has-photo', !payload._default);
    applySlotVisibility(slot);
    syncSlotPill(slotName);

    setPreviewStatus(slot, null);
    // Re-apply measurements toggle state after every (re)mount — the
    // bundle resets the overlay each time the scene rebuilds. Only
    // relevant in the frame3d slot (orbit view); wall mode ignores it.
    applySurgicalMeasurements();
    // First successful wall-photo mount — surface the coach-mark tip.
    if (slotName === 'wall' && !payload._default) maybeShowWallTip();
  }

  // ── Photo upload pipeline ─────────────────────────────────────────────

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error || new Error('file read failed'));
      r.readAsDataURL(file);
    });
  }

  // Mobile-camera photos carry an EXIF Orientation tag (typically 6 = rotate
  // 90° CW). The browser honours it when rendering an <img>, but the raw
  // bytes from FileReader/readAsDataURL still contain the un-rotated pixel
  // grid. The room-analysis backend reads pixels directly, so an iPhone
  // portrait photo arrives landscape-shaped and gets cropped to the wall
  // strip instead of the full room.
  //
  // Bake the EXIF orientation into the pixel grid client-side via
  // createImageBitmap({ imageOrientation: 'from-image' }) and re-encode as
  // a JPEG. The result has no EXIF tag and is laid out the way the user
  // sees it on-device — so the backend sees the same image.
  //
  // Falls back to the raw FileReader path on browsers that don't support
  // createImageBitmap with imageOrientation (older Safari) or if the bake
  // step throws for any reason.
  async function readFileAsOrientedDataUrl(file) {
    const MAX_DIM = 2048; // keep camera photos manageable but high-fidelity
    try {
      if (typeof createImageBitmap !== 'function') {
        return await readFileAsDataUrl(file);
      }
      let bitmap;
      try {
        bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      } catch (e) {
        // Some Safari versions throw on the options bag — retry without.
        bitmap = await createImageBitmap(file);
      }
      const srcW = bitmap.width;
      const srcH = bitmap.height;
      const scale = Math.min(1, MAX_DIM / Math.max(srcW, srcH));
      const w = Math.max(1, Math.round(srcW * scale));
      const h = Math.max(1, Math.round(srcH * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close?.();
      return canvas.toDataURL('image/jpeg', 0.92);
    } catch (e) {
      console.warn('[surgical-room-preview] EXIF normalize failed, sending raw bytes', e);
      return await readFileAsDataUrl(file);
    }
  }

  // fetch with a per-request timeout, optionally tied to an outer abort
  // signal. Without this a stalled request hangs its await forever — the
  // poll loops only check their 3-minute wall between iterations, so a
  // single hung fetch left the pill on "Analysing wall…" with no way out.
  async function fetchWithTimeout(url, opts, outerSignal, timeoutMs) {
    const ctrl = new AbortController();
    const onAbort = () => ctrl.abort();
    outerSignal?.addEventListener('abort', onAbort, { once: true });
    const timer = setTimeout(onAbort, timeoutMs);
    try {
      return await fetch(url, { ...opts, signal: ctrl.signal });
    } finally {
      clearTimeout(timer);
      outerSignal?.removeEventListener('abort', onAbort);
    }
  }
  const abortError = () => new DOMException('Flow superseded', 'AbortError');

  async function analyseRoom(dataUrl, onProgress, signal) {
    // Generous POST timeout — the body carries the room photo data URL,
    // which can take a while to upload on a slow mobile uplink.
    const postRes = await fetchWithTimeout(`${BACKEND_HOST}/api/room-analysis`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageDataUrl: dataUrl }),
    }, signal, 120000);
    if (!postRes.ok && postRes.status !== 202) {
      const txt = await postRes.text().catch(() => '');
      throw new Error(`room-analysis: ${postRes.status} ${txt.slice(0, 200)}`);
    }
    const initial = await postRes.json();
    if (initial.status === 'ready') return initial;
    if (initial.status !== 'processing') throw new Error(`room-analysis: unexpected status ${initial.status}`);

    const { photoHash } = initial;
    if (!photoHash) throw new Error('room-analysis: missing photoHash');

    const startMs = Date.now();
    const POLL_INTERVAL_MS = 3000;
    const TIMEOUT_MS = 3 * 60 * 1000;

    while (true) {
      const elapsed = (Date.now() - startMs) / 1000;
      onProgress?.(elapsed);
      if (Date.now() - startMs > TIMEOUT_MS) {
        throw new Error('room-analysis: timed out after 3 minutes');
      }
      await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
      if (signal?.aborted) throw abortError();
      let body;
      try {
        const pollRes = await fetchWithTimeout(
          `${BACKEND_HOST}/api/room-analysis?photoHash=${encodeURIComponent(photoHash)}`,
          {}, signal, 15000
        );
        if (!pollRes.ok) continue;
        body = await pollRes.json();
      } catch (e) {
        if (signal?.aborted) throw e;
        continue; // transient network/timeout/non-JSON body — try again next tick
      }
      if (body.status === 'ready') return body;
    }
  }

  // Loading state lives on the wall slot's pill so the user keeps the
  // current slide interactive while analysis runs.
  function setUploadBusy(busy, text) {
    const slot = slots.wall;
    if (!slot.pillEl) return;
    const wallTab = slot.pillEl.querySelector('[data-pill-action="wall"]');
    const labelEl = wallTab?.querySelector('.pdp-mode-tab__label');
    slot.pillEl.classList.toggle('pdp-mode-tabs--busy', busy);
    if (wallTab) {
      wallTab.classList.toggle('pdp-mode-tab--busy', busy);
      wallTab.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (labelEl && busy) labelEl.textContent = text || 'Analysing wall…';
    }
    if (!busy) syncSlotPill('wall'); // restores the right segment label
  }

  // Coach-mark — shown the first time a wall photo lands. Shared
  // localStorage flag with readymade so the user only sees it once across
  // the storefront. Auto-dismisses on 8s, on first canvas pointerdown, or
  // via the × button.
  const WALL_TIP_STORAGE_KEY = 'fy-wall-tip-seen-v2';
  const WALL_TIP_AUTO_HIDE_MS = 8000;
  const WALL_TIP_DRAG_DELAY_MS = 700;

  function maybeShowWallTip() {
    const tipEl = slots.wall.tipEl;
    if (!tipEl) return;
    let seen = false;
    try { seen = localStorage.getItem(WALL_TIP_STORAGE_KEY) === '1'; } catch { /* private mode */ }
    if (seen) return;

    tipEl.hidden = false;
    void tipEl.offsetWidth; // reflow so the opacity transition runs
    tipEl.classList.add('pdp-wall-tip--visible');

    let dismissed = false;
    const stage = slots.wall.stageEl;
    const hideTip = () => {
      if (dismissed) return;
      dismissed = true;
      tipEl.classList.remove('pdp-wall-tip--visible');
      setTimeout(() => { tipEl.hidden = true; }, 240);
      try { localStorage.setItem(WALL_TIP_STORAGE_KEY, '1'); } catch { /* noop */ }
      clearTimeout(autoTimer);
      tipEl.removeEventListener('click', onTipClick);
      stage?.removeEventListener('pointerdown', onStagePointer);
    };
    const onTipClick = (e) => {
      if (e.target.closest('.pdp-wall-tip__close')) hideTip();
    };
    const onStagePointer = () => hideTip();
    tipEl.addEventListener('click', onTipClick);
    setTimeout(() => {
      if (!dismissed) stage?.addEventListener('pointerdown', onStagePointer, { once: true });
    }, WALL_TIP_DRAG_DELAY_MS);
    const autoTimer = setTimeout(hideTip, WALL_TIP_AUTO_HIDE_MS);
  }

  async function startRoomFlow(file) {
    const wallSlot = slots.wall;
    // Last upload wins — abort a still-running flow so two concurrent flows
    // can't interleave analysis writes, busy flags, and mounts.
    roomPreviewState.flowAbort?.abort();
    const flowAbort = new AbortController();
    roomPreviewState.flowAbort = flowAbort;
    const { signal } = flowAbort;
    try {
      // Step labels live on the wall slot's pill so the user can keep
      // browsing the current slide while analysis runs. Inside the wall
      // overlay we also drop a scrim + status text in case the user
      // taps "Your wall" mid-analysis and lands on the spinner.
      setUploadBusy(true, 'Step 1 of 3 · Processing photo…');
      setPreviewStatus(wallSlot, 'loading', 'Step 1 of 3 · Processing photo…');
      const dataUrl = await readFileAsOrientedDataUrl(file);
      if (signal.aborted) return;

      setPreviewStatus(wallSlot, 'loading', 'Step 2 of 3 · Analysing wall…');
      const analysis = await analyseRoom(dataUrl, (elapsed) => {
        const s = Math.round(elapsed);
        let label;
        if (s >= 20)      label = `Step 2 of 3 · Analysing wall · ${s}s (still working…)`;
        else if (s >= 12) label = `Step 2 of 3 · Analysing wall · ${s}s`;
        else              label = 'Step 2 of 3 · Analysing wall…';
        setUploadBusy(true, label);
        setPreviewStatus(wallSlot, 'loading', label);
      }, signal);
      if (signal.aborted) return;

      roomPreviewState.analysis = analysis;
      persistWallAnalysis(analysis);
      syncSlotPill('wall'); // flip to 3-segment state immediately

      setUploadBusy(true, 'Step 3 of 3 · Rendering preview…');
      setPreviewStatus(wallSlot, 'loading', 'Step 3 of 3 · Rendering preview…');
      await mountRoomPreview('wall', buildMountPayload(analysis));
      if (signal.aborted) return;
      setUploadBusy(false);
      setPreviewStatus(wallSlot, null);
    } catch (err) {
      if (signal.aborted) return; // superseded — the newer flow owns the UI
      console.error('[surgical-room-preview] flow failed', err);
      setUploadBusy(false);
      setPreviewStatus(wallSlot, 'error', 'Could not create preview. Try a different photo.');
    }
  }

  function buildMountPayload(analysis) {
    return {
      envmapUrl: analysis.envmapUrl,
      depthMapUrl: analysis.depthMapUrl,
      depthMaxM: analysis.depthMaxM,
      analysis: analysis.analysis,
      lighting: null,
      frameConfig: buildFrameConfigPayload(),
      artworkUrl: currentArtworkUrl(),
      sizePhysical: pickSizeForPreview(),
      roomPhotoUrl: analysis.roomPhotoUrl,
      // Keep the full uploaded photo — bundle default zooms in on the focal
      // wall (advisor mobile UX). PDP shows the user's original framing.
      disableWallCrop: true,
      _sig: currentConfigSignature(),
    };
  }

  // Synthetic single-wall scene shown when no photo has been uploaded.
  // Identical wall pose + camera intrinsics to the readymade-frame-pdp
  // default — see assets/readymade-frame-pdp.js buildDefaultPayload() for
  // the rationale behind the 28° FOV and 15° wall yaw.
  function buildDefaultPayload() {
    return {
      envmapUrl: null,
      depthMapUrl: null,
      depthMaxM: null,
      analysis: {
        image_dims: { width: 1200, height: 1200 },
        intrinsics: { fov_y_deg: 28 },
        metric_scaling_factor: 1.0,
        wall_planes: [{
          corners_3d: [
            [-1.4489, -1.5, 1.4117],
            [ 1.4489, -1.5, 2.1883],
            [ 1.4489,  1.5, 2.1883],
            [-1.4489,  1.5, 1.4117],
          ],
          normal:   [0.2588, 0, -0.9659],
          centroid: [0, 0, 1.8],
        }],
        pipeline_version: 'synthetic',
      },
      lighting: { azimuth_deg: 40, elevation_deg: 35, kelvin: 3800, intensity: 0.6 },
      frameConfig: buildFrameConfigPayload(),
      artworkUrl: currentArtworkUrl(),
      sizePhysical: pickSizeForPreview(),
      roomPhotoUrl: null,
      wallColor: getWallColor(),
      _sig: currentConfigSignature(),
      _default: true,
    };
  }

  // Push the current configurator state into whichever slot currently
  // holds the bundle. Debounced so rapid size-button clicks don't thrash
  // setFrame's async texture reloads.
  let refreshTimer = null;
  function refreshRoomPreview() {
    if (!roomPreviewState.instance || !roomPreviewState.bundleSlot) return;
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      const payload = roomPreviewState.bundleSlot === 'wall'
        ? buildMountPayload(roomPreviewState.analysis)
        : buildDefaultPayload();
      roomPreviewState.lastPayload = payload;
      roomPreviewState.instance.update(payload)
        .then(() => applySurgicalMeasurements())
        .catch(() => {});
    }, 120);
  }

  // ── Event wiring ───────────────────────────────────────────────────────

  function bindSlotControls() {
    bindSlot(slots.frame3d);
    bindSlot(slots.wall);
  }

  function bindSlot(slot) {
    // Zoom button binds BEFORE the pill guard: with new_pdp_hide_gallery_mode
    // the Frame/3D pill isn't rendered at all, and the early-return below used
    // to dead-end before reaching the zoom wiring — leaving the lightbox
    // unreachable (slide 0's on:click is stripped, the button is the only
    // entry point).
    if (slot.name === 'frame3d' && slot.zoomBtnEl && !slot.zoomBtnEl.dataset.pdpBound) {
      slot.zoomBtnEl.dataset.pdpBound = '1';
      ['mousedown', 'pointerdown', 'touchstart'].forEach(evt => {
        slot.zoomBtnEl.addEventListener(evt, (e) => e.stopPropagation());
      });
      slot.zoomBtnEl.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
          mediaGallery?.zoom?.(0, e);
        } catch (err) {
          console.warn('[surgical-room-preview] zoom failed', err);
        }
      });
    }

    if (!slot.pillEl || slot.pillEl.dataset.pdpBound === '1') return;
    slot.pillEl.dataset.pdpBound = '1';

    // Stop pointer events from leaking into the slide's swipe-to-change
    // handler / on:click zoom — applies to both pill and overlay.
    const stopPropagation = (el) => {
      if (!el) return;
      el.addEventListener('click', (e) => e.stopPropagation());
      ['mousedown', 'pointerdown', 'touchstart'].forEach(evt => {
        el.addEventListener(evt, (e) => e.stopPropagation());
      });
    };
    stopPropagation(slot.pillEl);
    stopPropagation(slot.overlayEl);
    stopPropagation(slot.zoomBtnEl);

    // Pill click router.
    slot.pillEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pill-action]');
      if (!btn) return;
      // Ignore disabled / busy segments.
      if (btn.classList.contains('pdp-mode-tab--busy')) return;
      e.preventDefault();
      const action = btn.dataset.pillAction;

      if (slot.name === 'frame3d') {
        if (action === 'frame')          exitSlot('frame3d');
        else if (action === '3d')        enterSlot('frame3d');
        else if (action === 'rotate-left')  simulateCanvasDrag('left');
        else if (action === 'rotate-right') simulateCanvasDrag('right');
      } else if (slot.name === 'wall') {
        if (action === 'example') {
          exitSlot('wall');
        } else if (action === 'wall') {
          if (roomPreviewState.analysis) enterSlot('wall');
          else slot.fileInputEl?.click();
        } else if (action === 'change-photo') {
          slot.fileInputEl?.click();
        }
      }
    });

    // Slot-1 specific bindings — file input wiring lives here.
    if (slot.name === 'wall' && slot.fileInputEl) {
      slot.fileInputEl.addEventListener('change', (ev) => {
        const file = ev.target.files?.[0];
        if (file) startRoomFlow(file);
        ev.target.value = ''; // allow re-selecting the same file
      });
    }
  }

  // ── Measurements toggle ─────────────────────────────────────────────────
  // The 3D bundle exposes showMeasurements({mouldingMm, mountMm, …labels}) —
  // we derive labels from the live configurator (size + frame moulding) and
  // toggle them on/off from the chip next to the mode tabs. Off by default.
  let surgicalMeasuresVisible = false;

  function getSurgicalMeasureData() {
    const sp = getSkuPicker();
    // Prefer the picker's confirmed state; fall back to the URL "size" param
    // (server defaults to inches handles like "12-x-16"). Either way we end
    // up with a sane WxH in inches we can render labels from.
    const sizeHandle = sp?.state?.confirmed?.size_option
      || new URL(location.href).searchParams.get('size')
      || '';
    const parts = String(sizeHandle).split(/[x×-]/).map(Number).filter(n => n > 0);
    const paperWIn = parts[0] || 12;
    const paperHIn = parts[1] || 16;

    // Match readymade defaults — unframed art prints don't carry a moulding,
    // so when no frame is selected we suppress the moulding bracket entirely.
    // Bumped framed mouldingMm from 15 → 25 (d0ea): the previous 15mm
    // overhang minus the 12.7mm rebate left only ~0.18" of outer-over-paper
    // overhang, so 24" paper showed "24.2"" outer vs "24"" paper — close
    // enough to read as identical labels. 25mm gives a clear ~1" overhang
    // that matches the visual frame profile and yields distinct labels.
    const isFramed = sp?.state?.confirmed?.framing && sp.state.confirmed.framing !== 'unframed';
    const mouldingMm = isFramed ? 25 : 0;
    const mountMm    = 0; // surgical PDP doesn't expose a mount picker
    const REBATE_MM  = 12.7;
    const overhang   = Math.max(0, mouldingMm - REBATE_MM);
    const overallWIn = (paperWIn * 25.4 + 2 * overhang) / 25.4;
    const overallHIn = (paperHIn * 25.4 + 2 * overhang) / 25.4;
    // Always render to 1 decimal so labels honestly reflect the rounding —
    // matches the readymade PDP. "20"" reads as exactly 20.0" but the
    // actual size is typically 19.9 or 20.1; the trailing ".0" tells the
    // user the value is rounded, which matters when checking fit.
    const cleanIn = (n) => `${(Math.round(n * 10) / 10).toFixed(1)}"`;
    return {
      mouldingMm,
      mountMm,
      mouldingLabel: mouldingMm ? `${mouldingMm}mm` : '',
      mountLabel: '',
      overallWLabel: cleanIn(overallWIn),
      overallHLabel: cleanIn(overallHIn),
      paperWLabel: cleanIn(paperWIn),
      paperHLabel: cleanIn(paperHIn),
    };
  }

  function applySurgicalMeasurements() {
    const inst = roomPreviewState.instance;
    if (!inst?.showMeasurements) return;
    // Measurements only make sense in the orbit (frame3d) view — the bundle
    // ignores them in wall composite mode anyway.
    if (surgicalMeasuresVisible && roomPreviewState.bundleSlot === 'frame3d') {
      inst.showMeasurements(true, getSurgicalMeasureData());
    } else {
      inst.showMeasurements(false);
    }
  }

  function bindMeasureToggle() {
    // The measure toggle was injected into slot frame3d's overlay by the
    // template — find it inside the slide rather than at document scope.
    const btn = slots.frame3d.slideEl?.querySelector('#pdp-surgical-measure-toggle');
    if (!btn || btn.dataset.pdpBound === '1') return;
    btn.dataset.pdpBound = '1';
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      surgicalMeasuresVisible = !surgicalMeasuresVisible;
      btn.setAttribute('aria-pressed', String(surgicalMeasuresVisible));
      applySurgicalMeasurements();
    });
  }

  // Refresh the 3D scene whenever the configurator changes (size, frame,
  // border, mount). Mirrors the readymade-frame-pdp call to refreshRoomPreview()
  // inside selectSizeOption / colour-pick / border-pick handlers.
  function bindConfiguratorEvents() {
    const events = [
      'variant:change',
      'sku-picker:selection-changed',
      'sku-picker:variant-changed',
      'style-picker:applied',
      'gallery-wall:config-changed',
    ];
    events.forEach((evt) => {
      document.addEventListener(evt, refreshRoomPreview);
    });

    // Keep the synthetic-wall colour in sync with the 2D wall-colour picker
    // (only meaningful in the frame3d slot — the wall composite is governed
    // by the user's uploaded photo).
    document.addEventListener('wall-color-change', (e) => {
      if (!e.detail?.hex) return;
      if (roomPreviewState.bundleSlot !== 'frame3d') return;
      roomPreviewState.instance?.setWallColor?.(e.detail.hex);
    });
  }

})();
